/**
 * Browser fingerprint collector — NON BLOCCANTE.
 *
 * Vincoli assoluti:
 * - Ogni operazione singola è dentro try/catch: se una fonte fallisce ritorno null
 *   per quel campo e continuo con le altre.
 * - Timeout globale (default 2s): se scade, ritorna quello che ha raccolto fino a lì
 *   (parziale è ok — il server accetta anche null).
 * - Nessuna throw fuori dalla funzione: worst-case ritorna `null` all'intero payload.
 * - Nessun `await` che possa bloccare il rendering: il chiamante NON deve fare
 *   `await collectFingerprintSafe()` in un useEffect di primo rendering — deve usarlo
 *   fire-and-forget dopo che le domande sono già visibili.
 *
 * Rilevati (best-effort, tutti opzionali):
 *   fingerprint       — sha256 aggregata (senza subtle-crypto fallback: hash 32bit)
 *   timezone          — Intl.DateTimeFormat().resolvedOptions().timeZone
 *   language          — navigator.language
 *   screen            — "WxH" (window.screen)
 *   color_depth       — window.screen.colorDepth
 *   hardware_concurrency — navigator.hardwareConcurrency
 *   webgl_renderer    — WEBGL_debug_renderer_info.UNMASKED_RENDERER_WEBGL
 *   canvas_hash       — hash di un canvas.toDataURL con testo+shape
 *
 * GDPR: base giuridica legittimo interesse, art. 6.1.f. Usato solo per prevenire
 * usi impropri del questionario. Nessuna profilazione pubblicitaria.
 */

function safe(fn, fallback = null) {
  try { return fn(); } catch { return fallback; }
}

/** Hash veloce non-crittografico (djb2 xor) — 32bit hex. Fallback quando subtle non c'è. */
function hash32(input) {
  const s = String(input || "");
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i);
    h = h | 0; // int32
  }
  return (h >>> 0).toString(16);
}

/** Se disponibile, usa crypto.subtle SHA-256 (hex). Altrimenti fallback djb2. */
async function digestHex(input) {
  try {
    if (window.crypto?.subtle && typeof TextEncoder !== "undefined") {
      const buf = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(String(input || "")));
      return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, "0")).join("");
    }
  } catch { /* ignore */ }
  return hash32(input);
}

function collectCanvasHash() {
  return safe(() => {
    if (typeof document === "undefined") return null;
    const c = document.createElement("canvas");
    // dimensioni piccole per essere veloci
    c.width = 220; c.height = 40;
    const ctx = c.getContext("2d");
    if (!ctx) return null;
    ctx.textBaseline = "top";
    ctx.font = "14px 'Arial'";
    ctx.fillStyle = "#0D1F3C";
    ctx.fillRect(0, 0, 220, 40);
    ctx.fillStyle = "#f0f";
    ctx.fillText("Selly.canvas.fp \u2603", 4, 4);
    ctx.strokeStyle = "#4faf";
    ctx.beginPath();
    ctx.arc(180, 20, 12, 0, Math.PI * 2, true);
    ctx.stroke();
    // toDataURL può essere bloccato (privacy.resistFingerprinting in Firefox, alcune estensioni)
    const url = c.toDataURL();
    if (!url || url.length < 50) return null;
    // hash veloce sync — SHA-256 asincrono lo faremo sull'aggregato
    return hash32(url);
  });
}

function collectWebglRenderer() {
  return safe(() => {
    if (typeof document === "undefined") return null;
    const c = document.createElement("canvas");
    const gl = c.getContext("webgl") || c.getContext("experimental-webgl");
    if (!gl) return null;
    // Extension WEBGL_debug_renderer_info; alcuni browser (Firefox strict) la bloccano
    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
    if (!dbg) return safe(() => gl.getParameter(gl.RENDERER)) || null;
    const r = gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL);
    return r ? String(r).slice(0, 128) : null;
  });
}

/**
 * Raccoglie il fingerprint. Ritorna sempre un oggetto (mai throw) o null se il timeout scade.
 * @param {number} timeoutMs - default 2000
 * @returns {Promise<object|null>}
 */
export async function collectFingerprintSafe(timeoutMs = 2000) {
  const timeoutPromise = new Promise(resolve => setTimeout(() => resolve(null), Math.max(500, timeoutMs)));
  const worker = (async () => {
    const parts = {
      timezone: safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone) || null,
      language: safe(() => navigator.language) || null,
      screen: safe(() => (window.screen ? `${window.screen.width}x${window.screen.height}` : null)) || null,
      color_depth: safe(() => window.screen?.colorDepth) ?? null,
      hardware_concurrency: safe(() => navigator.hardwareConcurrency) ?? null,
      webgl_renderer: collectWebglRenderer(),
      canvas_hash: collectCanvasHash(),
      // B73 M1 — segnale (non blocco) per detection ambiente automatizzato
      webdriver: safe(() => !!navigator.webdriver) === true,
    };
    // Aggregato per il campo `fingerprint`
    const aggregate = [
      parts.timezone, parts.language, parts.screen, parts.color_depth,
      parts.hardware_concurrency, parts.webgl_renderer, parts.canvas_hash,
      safe(() => navigator.userAgent) || "",
    ].join("|");
    const fingerprint = await digestHex(aggregate);
    return { fingerprint, ...parts };
  })();
  try {
    return await Promise.race([worker, timeoutPromise]);
  } catch {
    return null;
  }
}

/**
 * Fire-and-forget: raccoglie il fingerprint (max 2s) e lo posta al backend.
 * Il chiamante NON deve `await` questa funzione. Se il POST fallisce, silenzio.
 *
 * @param {string} apiBase - REACT_APP_BACKEND_URL
 * @param {string} testLink - test_link (candidato/dipendente)
 */
export function sendFingerprintFireAndForget(apiBase, testLink) {
  // Micro-defer: cede il tick al render (assicura che le domande siano già visibili)
  setTimeout(async () => {
    try {
      const payload = await collectFingerprintSafe(2000);
      if (!payload || !payload.fingerprint) return; // niente da mandare → skip silenzioso
      // POST con timeout AbortController (2s), errori ignorati
      const ctrl = new AbortController();
      const to = setTimeout(() => ctrl.abort(), 2000);
      try {
        await fetch(`${apiBase}/api/test/session/${encodeURIComponent(testLink)}/fingerprint`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          signal: ctrl.signal,
          // credentials omessi: sessione pubblica anonima
        });
      } catch { /* rete offline / timeout / 4xx → ignora */ }
      finally { clearTimeout(to); }
    } catch { /* worst-case totale: silenzio */ }
  }, 100);
}

export default sendFingerprintFireAndForget;
